// Load a (possibly cross-origin) image into a base64 PNG data URL so it can
// be embedded in a jsPDF document. jsPDF's addImage() cannot fetch a remote
// URL itself — passing one directly only "works" by accident, depending on
// CORS headers and load timing, which is why some images silently failed to
// appear on exported PDFs while others didn't.
export const loadImageAsDataUrl = (imageSrc) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            } catch (error) {
                reject(error);
            }
        };

        img.onerror = () => reject(new Error(`Failed to load image: ${imageSrc}`));

        img.src = imageSrc;
    });
};

// Enhanced color extraction utility for dynamic branding
export const extractLogoColors = async (imageSrc) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Set canvas size to match image
                canvas.width = img.width;
                canvas.height = img.height;
                
                // Draw image to canvas
                ctx.drawImage(img, 0, 0);
                
                // Get image data
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;
                
                // Extract colors using advanced color quantization
                const colors = extractDominantColors(data, canvas.width, canvas.height);
                
                resolve(colors);
            } catch (error) {
                console.error('Error extracting colors:', error);
                resolve(getDefaultColorPalette());
            }
        };
        
        img.onerror = () => {
            console.error('Error loading image for color extraction');
            resolve(getDefaultColorPalette());
        };
        
        img.src = imageSrc;
    });
};

// Advanced color quantization algorithm
const extractDominantColors = (imageData, width, height) => {
    const colorMap = new Map();
    const pixelCount = width * height;
    
    // Sample every 4th pixel for performance (adjust for accuracy vs speed)
    const sampleRate = 4;
    
    for (let i = 0; i < imageData.length; i += 4 * sampleRate) {
        const r = imageData[i];
        const g = imageData[i + 1];
        const b = imageData[i + 2];
        const a = imageData[i + 3];
        
        // Skip transparent or near-transparent pixels
        if (a < 128) continue;
        
        // Skip very light colors (likely background)
        if (r > 240 && g > 240 && b > 240) continue;
        
        // Create color key with slight quantization for similar colors
        const colorKey = `${Math.floor(r/10)*10}-${Math.floor(g/10)*10}-${Math.floor(b/10)*10}`;
        
        if (colorMap.has(colorKey)) {
            colorMap.set(colorKey, {
                ...colorMap.get(colorKey),
                count: colorMap.get(colorKey).count + 1
            });
        } else {
            colorMap.set(colorKey, { r, g, b, count: 1 });
        }
    }
    
    // Sort colors by frequency and get top colors
    const sortedColors = Array.from(colorMap.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
    
    // Filter and enhance colors
    const processedColors = processExtractedColors(sortedColors);
    
    return createColorPalette(processedColors);
};

// Process and enhance extracted colors
const processExtractedColors = (colors) => {
    const processed = [];
    
    for (const color of colors) {
        const { r, g, b } = color;
        
        // Calculate color properties
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const saturation = calculateSaturation(r, g, b);
        
        // Skip very dark or very light colors
        if (luminance < 0.1 || luminance > 0.9) continue;
        
        // Prefer more saturated colors
        if (saturation < 0.2) continue;
        
        processed.push({
            r, g, b,
            luminance,
            saturation,
            hue: calculateHue(r, g, b)
        });
    }
    
    return processed;
};

// Calculate color saturation
const calculateSaturation = (r, g, b) => {
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    return max === 0 ? 0 : (max - min) / max;
};

// Calculate color hue
const calculateHue = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const diff = max - min;
    
    if (diff === 0) return 0;
    
    let hue;
    switch (max) {
        case r: hue = (g - b) / diff + (g < b ? 6 : 0); break;
        case g: hue = (b - r) / diff + 2; break;
        case b: hue = (r - g) / diff + 4; break;
    }
    
    return hue * 60;
};

// Create a sophisticated color palette from extracted colors
const createColorPalette = (colors) => {
    if (colors.length === 0) return getDefaultColorPalette();
    
    // Select primary color (most prominent, well-saturated)
    const primaryColor = colors[0];
    
    // Select secondary color (different hue from primary)
    let secondaryColor = colors.find(c => 
        Math.abs(c.hue - primaryColor.hue) > 30 && 
        Math.abs(c.hue - primaryColor.hue) < 330
    ) || colors[1] || primaryColor;
    
    // Generate accent colors
    const accentColor = generateAccentColor(primaryColor, secondaryColor);
    const successColor = generateSuccessColor(colors);
    const warningColor = generateWarningColor(colors);
    
    return {
        primary: [primaryColor.r, primaryColor.g, primaryColor.b],
        secondary: [secondaryColor.r, secondaryColor.g, secondaryColor.b],
        accent: accentColor,
        success: successColor,
        warning: warningColor,
        text: [31, 41, 55],
        lightGray: [248, 250, 252],
        mediumGray: [226, 232, 240],
        darkGray: [100, 116, 139],
        // Additional sophisticated colors
        primaryLight: lightenColor([primaryColor.r, primaryColor.g, primaryColor.b], 0.3),
        secondaryLight: lightenColor([secondaryColor.r, secondaryColor.g, secondaryColor.b], 0.3),
        primaryDark: darkenColor([primaryColor.r, primaryColor.g, primaryColor.b], 0.2),
        gradientStart: [primaryColor.r, primaryColor.g, primaryColor.b],
        gradientEnd: [secondaryColor.r, secondaryColor.g, secondaryColor.b]
    };
};

// Generate accent color with proper contrast
const generateAccentColor = (primary, secondary) => {
    // Create a vibrant accent that contrasts well
    const avgR = (primary.r + secondary.r) / 2;
    const avgG = (primary.g + secondary.g) / 2;
    const avgB = (primary.b + secondary.b) / 2;
    
    // Shift towards red/orange for accent
    return [
        Math.min(255, Math.floor(avgR * 1.2)),
        Math.min(255, Math.floor(avgG * 0.8)),
        Math.min(255, Math.floor(avgB * 0.6))
    ];
};

// Generate success color (greenish)
const generateSuccessColor = (colors) => {
    const greenish = colors.find(c => c.hue > 80 && c.hue < 160);
    return greenish ? [greenish.r, greenish.g, greenish.b] : [34, 197, 94];
};

// Generate warning color (yellowish/orange)
const generateWarningColor = (colors) => {
    const yellowish = colors.find(c => c.hue > 30 && c.hue < 80);
    return yellowish ? [yellowish.r, yellowish.g, yellowish.b] : [251, 191, 36];
};

// Color manipulation utilities
export const lightenColor = (color, factor) => {
    return color.map(c => Math.min(255, Math.floor(c + (255 - c) * factor)));
};

export const darkenColor = (color, factor) => {
    return color.map(c => Math.floor(c * (1 - factor)));
};

// Default fallback palette
export const getDefaultColorPalette = () => {
    return {
        primary: [63, 61, 165],
        secondary: [99, 102, 241],
        accent: [239, 68, 68],
        success: [34, 197, 94],
        warning: [251, 191, 36],
        text: [31, 41, 55],
        lightGray: [248, 250, 252],
        mediumGray: [226, 232, 240],
        darkGray: [100, 116, 139],
        primaryLight: [116, 114, 200],
        secondaryLight: [139, 142, 251],
        primaryDark: [50, 49, 132],
        gradientStart: [63, 61, 165],
        gradientEnd: [99, 102, 241]
    };
};

// Convert RGB array to CSS color string
export const rgbToCss = (rgbArray) => {
    return `rgb(${rgbArray[0]}, ${rgbArray[1]}, ${rgbArray[2]})`;
};

// Convert RGB array to hex color string
export const rgbToHex = (rgbArray) => {
    const r = rgbArray[0].toString(16).padStart(2, '0');
    const g = rgbArray[1].toString(16).padStart(2, '0');
    const b = rgbArray[2].toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
};
